import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  Menu,
  MenuItem,
  Paper,
  Popover,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  makeStyles
} from '@material-ui/core'
import MoreVertIcon from '@material-ui/icons/MoreVert'
import Dayjs from 'dayjs'
import React from 'react'

import {
  decideConsumables,
  decideOrder,
  fetchHqBoardExport,
  fetchHqMonthlyBoard,
  fetchHqMonthlyBoardDetail,
  fetchHqOrderTimeline,
  fetchPettyCash,
  hqOrderAction,
  orderAdminAction,
  savePettyCashAllocation,
  setPettyCashFreeze
} from 'api/procurement'
import { confirmDialog, notify } from 'app/helpers'
import { money } from 'app/pages/Apartment/Procurement/constants'
import AttachmentUploader from 'app/pages/Procurement/components/AttachmentUploader'
import ExportButtons from 'app/pages/Procurement/components/ExportButtons'
import {
  downloadOrderPdf,
  downloadOrderPdfSections,
  downloadPdfSections,
  downloadXlsxSheets,
  linkCell
} from 'app/pages/Procurement/components/exportUtils'
import { rmPaidByLabel } from 'app/pages/Procurement/constants'
import HqRmReco from './HqRmReco'
import HqRmRegister from './HqRmRegister'

// One all-centres monthly board per category, in a single rich table (PO ·
// centre · month · items · total · status · actions), matching the consumables
// view. Order categories (PM, Consumables) approve/reject; ledgers (Petty Cash,
// R&M) show spend + freeze; Petty Cash also sets the monthly allocation.
export const META = {
  not_filled: { label: 'Not filled', color: '#c9791f', dashed: true },
  no_entries: { label: 'No entries', color: '#c9791f', dashed: true },
  draft: { label: 'Draft', color: '#9c988f' },
  submitted: { label: 'Waiting for HQ', color: '#8d6e00', bg: '#fff8e1' },
  approved: { label: 'Approved', color: '#2e7d32', bg: '#e8f5e9' },
  rejected: { label: 'Rejected', color: '#c62828', bg: '#ffebee' },
  sent: { label: 'Sent', color: '#5e35b1', bg: '#ede7f6' },
  // Capex wording for 'sent' (HQ orders from vendors, nothing is dispatched).
  ordered: { label: 'Ordered', color: '#5e35b1', bg: '#ede7f6' },
  received: { label: 'Received', color: '#1565c0', bg: '#e3f2fd' },
  fulfilled: { label: 'Fulfilled', color: '#1565c0', bg: '#e3f2fd' },
  open: { label: 'Open', color: '#2e7d32', bg: '#e8f5e9' },
  frozen: { label: 'Frozen', color: '#1565c0', bg: '#e3f2fd' }
}
const CATS = {
  pm: { label: 'Program Material', ledger: false },
  consumables: { label: 'Consumables', ledger: false },
  petty_cash: { label: 'Petty Cash', ledger: true, alloc: true },
  rm: { label: 'R&M', ledger: true }
}

// ---- PO register (user 2026-08-22): the order-category downloads are ORDER
// documents, not table dumps — # | Product | Unit | Qty | Rate | Total |
// Remarks with the PO/centre/month in the header block, one order per centre.
// Ledger categories (Petty Cash, R&M) have no PO and keep the plain export.
export const REGISTER_HEADERS = [
  '#',
  'Product',
  'Unit',
  'Qty',
  'Rate (Rs)',
  'Total (Rs)',
  'Remarks'
]
export const REGISTER_ALIGN = [
  'right',
  false,
  false,
  'right',
  'right',
  'right',
  false
]
// Backend detail rows arrive as arrays in that endpoint's own column order —
// PM is [Item, Qty, Unit, Unit cost, Total, Remarks], Consumables is [Item,
// Unit, Qty, Unit price, Total] — so map by column NAME into the one register.
// Only Program Material carries a reference link — Consumables has no such
// field, and a column that is blank in every row reads as missing data rather
// than as "not applicable here". So the register grows a Link column only when
// the backend actually sent one, and it goes LAST so Total stays at index 5
// and the totals row below is unaffected.
export const isUrl = (v) =>
  typeof v === 'string' && /^https?:\/\//i.test(v.trim())
const hasLinkColumn = (columns) =>
  (columns || []).some((c) => String(c).toLowerCase() === 'link')
export const registerHeaders = (columns) =>
  hasLinkColumn(columns) ? [...REGISTER_HEADERS, 'Link'] : REGISTER_HEADERS
export const registerAlign = (columns) =>
  hasLinkColumn(columns) ? [...REGISTER_ALIGN, false] : REGISTER_ALIGN
const registerRows = (columns, rows) => {
  const ix = {}
  columns.forEach((c, i) => {
    ix[String(c).toLowerCase()] = i
  })
  const pick = (row, ...names) => {
    for (const n of names) if (ix[n] !== undefined) return row[ix[n]]
    return ''
  }
  const withLink = hasLinkColumn(columns)
  return rows.map((r, i) => {
    const reg = [
      i + 1,
      pick(r, 'item'),
      pick(r, 'unit'),
      pick(r, 'qty'),
      pick(r, 'unit cost', 'unit price'),
      pick(r, 'total'),
      pick(r, 'remarks')
    ]
    if (withLink) {
      // A real hyperlink in both the xlsx and the PDF; a line submitted with a
      // photo instead of a link carries its own wording and stays plain text.
      const link = pick(r, 'link')
      reg.push(isUrl(link) ? linkCell(link) : link || '')
    }
    return reg
  })
}
const registerTotals = (regRows, columns) => [
  'Total',
  null,
  null,
  null,
  null,
  String(Math.round(regRows.reduce((s, r) => s + (Number(r[5]) || 0), 0))),
  null,
  ...(hasLinkColumn(columns) ? [null] : [])
]
// The pre-table header block for the Excel register sheet.
const registerPre = (label, po, centreName, monthLabel) => [
  [`${label} order`],
  ['PO', po || ''],
  ['Centre', centreName],
  ['Month', monthLabel],
  []
]

const useStyles = makeStyles((theme) => ({
  bar: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    flexWrap: 'wrap',
    marginBottom: theme.spacing(1.5)
  },
  spacer: { flexGrow: 1 },
  summary: { display: 'flex', gap: theme.spacing(1.5), flexWrap: 'wrap' },
  cell: { padding: '9px 12px' },
  // PO numbers must stay on one line.
  poCell: { whiteSpace: 'nowrap' },
  centre: { fontWeight: 650, fontSize: 14 },
  meta: { fontSize: 11.5, color: '#9c988f', marginTop: 2 },
  acts: { display: 'flex', gap: 6, alignItems: 'center' },
  alloc: { width: 90, '& input': { fontSize: 13, padding: '4px 6px' } },
  // Page-level freeze panes (user 2026-07-21): the header row sticks to the top
  // of the screen on scroll-down and the Sl No. + Centre columns stick to the
  // left on scroll-right. The table is NOT in an overflow box, so the freezes
  // are relative to the page (the doc scrolls), matching the confirmed mock.
  stickyTable: {
    borderCollapse: 'separate',
    borderSpacing: 0,
    '& thead th': {
      position: 'sticky',
      top: 0,
      zIndex: 5,
      background: theme.palette.grey[100],
      boxShadow: 'inset 0 -1px 0 #e0ddd6'
    },
    '& td': { boxShadow: 'inset 0 -1px 0 #eceae4' },
    // Zebra striping. A sticky cell needs its own opaque background or the
    // scrolling content shows through it, so the stripe is painted on the CELLS
    // rather than the row — that way the frozen Sl No. / Centre columns carry
    // the same band as the rest of the row instead of staying white.
    '& tbody tr:nth-of-type(odd) td': {
      backgroundColor: theme.palette.grey[50]
    },
    '& tbody tr:nth-of-type(even) td': {
      backgroundColor: theme.palette.background.paper
    },
    // Keep the row-hover affordance, which the cell backgrounds would otherwise
    // cover. Must stay AFTER the stripes: equal specificity, so source order wins.
    '& tbody tr:hover td': {
      backgroundColor: theme.palette.action.hover
    }
  },
  frz1: {
    position: 'sticky',
    left: 0,
    zIndex: 4,
    width: 60,
    minWidth: 60
  },
  frz2: {
    position: 'sticky',
    left: 60,
    zIndex: 4,
    boxShadow: 'inset 0 -1px 0 #eceae4, 8px 0 8px -8px rgba(0,0,0,.15)'
  },
  frzHead1: { position: 'sticky', left: 0, top: 0, zIndex: 7 },
  frzHead2: {
    position: 'sticky',
    left: 60,
    top: 0,
    zIndex: 7,
    boxShadow: 'inset 0 -1px 0 #e0ddd6, 8px 0 8px -8px rgba(0,0,0,.15)'
  }
}))

export const StatusChip = ({ s }) => {
  const m = META[s] || { label: s, color: '#666' }
  return (
    <Chip
      size="small"
      label={m.label}
      style={{
        background: m.dashed ? 'transparent' : m.bg || 'transparent',
        color: m.color,
        border: m.dashed ? `1px dashed ${m.color}` : '1px solid transparent',
        height: 22,
        fontSize: 11.5,
        fontWeight: 600
      }}
    />
  )
}

const HqMonthlyBoard = ({ category = 'pm' }) => {
  const classes = useStyles()
  const cfg = CATS[category] || CATS.pm
  const [month, setMonth] = React.useState(Dayjs().format('YYYY-MM'))
  // R&M only: Azam's sheet / reco, opened from the buttons beside Excel.
  const [rmView, setRmView] = React.useState(null)
  const [rows, setRows] = React.useState(null)
  const [loading, setLoading] = React.useState(false)
  const [busyId, setBusyId] = React.useState(null)
  const [allocEdits, setAllocEdits] = React.useState({})
  // The "view list" popup: the centre whose lines are open + the fetched detail.
  const [detail, setDetail] = React.useState(null) // {centre_name, data|null}
  // Ledger categories only: the centre's spend lines with their bill counts, so
  // HQ can open the proof a centre attached. {centre_name, rows|null}
  const [bills, setBills] = React.useState(null)
  const [billEntry, setBillEntry] = React.useState(null) // the line being viewed
  // Row actions menu (⋮) and the petty-cash allocation popover.
  const [menu, setMenu] = React.useState(null) // {el, row}
  const [allocPop, setAllocPop] = React.useState(null) // {el, row}
  // Status tracking popup (PM/consumables): the order's dated lifecycle + HQ
  // remark editing + resend-on-discrepancy.
  const [track, setTrack] = React.useState(null) // {centre_name, row, data|null}
  const [remarkDraft, setRemarkDraft] = React.useState('')
  const [savingTrack, setSavingTrack] = React.useState(false)
  const [exporting, setExporting] = React.useState(false)

  const monthDate = `${month}-01`
  const monthLabel = Dayjs(monthDate).format('MMMM YYYY')
  const load = React.useCallback(() => {
    setLoading(true)
    fetchHqMonthlyBoard(monthDate)
      .then((d) => {
        setRows(d.results || [])
        setAllocEdits({})
      })
      .catch(() => setRows([]))
      .finally(() => setLoading(false))
  }, [monthDate])
  React.useEffect(load, [load])

  const list = rows || []
  // Per-category view model for a centre row.
  const vm = (r) => {
    if (category === 'petty_cash') {
      const p = r.petty_cash || {}
      const filled =
        Number(p.spent || 0) !== 0 || Number(p.allocation || 0) !== 0
      return {
        po: '—',
        items: null,
        total: p.spent,
        status: !filled ? 'no_entries' : p.frozen ? 'frozen' : 'open',
        frozen: p.frozen,
        allocation: p.allocation,
        inDraft: p.in_draft,
        submitted: p.submitted_by || ''
      }
    }
    if (category === 'rm') {
      const p = r.rm || {}
      const filled = Number(p.spent || 0) !== 0
      return {
        po: '—',
        items: null,
        total: p.spent,
        status: !filled ? 'no_entries' : p.frozen ? 'frozen' : 'open',
        frozen: p.frozen,
        inDraft: p.in_draft,
        submitted: p.submitted_by || ''
      }
    }
    const o = r[category] || {}
    return {
      po: o.po_number,
      items: o.items,
      total: o.total,
      status: o.status,
      submitted: o.submitted_by || ''
    }
  }

  const notFilled = list.filter((r) => {
    const s = vm(r).status
    return s === 'not_filled' || s === 'no_entries'
  }).length
  const waiting = cfg.ledger
    ? 0
    : list.filter((r) => vm(r).status === 'submitted').length

  // Whether a category row has any ⋮ action: ledgers always (freeze / allocate),
  // orders when submitted (approve / reject) and — PM + consumables — when
  // approved, so HQ can Send the order to the centre (which then confirms
  // receipt).
  // PM: HQ can reopen for the centre even after sending / receipt (user
  // 2026-11-03) — items already received stay received.
  const PM_REOPENABLE = ['approved', 'sent', 'received']
  const canReopen = (v) => category === 'pm' && PM_REOPENABLE.includes(v.status)
  const hasMenu = (v) =>
    cfg.ledger ||
    v.status === 'submitted' ||
    canReopen(v) ||
    ((category === 'pm' || category === 'consumables') &&
      v.status === 'approved')

  const reopenPm = async (r) => {
    const afterSend = ['sent', 'received'].includes(vm(r).status)
      ? ' Items already received stay received. You will need to approve and send it again.'
      : ''
    const { isConfirmed } = await confirmDialog({
      text: `Reopen — ${cfg.label} for ${r.centre_name}? The centre can edit and resubmit.${afterSend}`,
      confirmButtonText: 'Yes',
      options: { title: 'Please confirm' }
    })
    if (!isConfirmed) return
    setBusyId(r.centre)
    try {
      await orderAdminAction(r.centre, monthDate, 'reopen')
      notify(`${cfg.label} reopened for ${r.centre_name}`, '', 'success')
      load()
    } finally {
      setBusyId(null)
    }
  }

  const DECIDE_LABELS = {
    approved: 'Approve',
    rejected: 'Reject',
    sent: 'Send'
  }
  const decide = async (r, decision) => {
    const label = DECIDE_LABELS[decision] || decision
    const verb = decision === 'sent' ? 'Send to the centre' : label
    // confirm() from helpers is callback-style and returns undefined; awaiting it
    // always resolved falsy, so this decision handler used to bail before ever
    // calling the API. confirmDialog() actually resolves {isConfirmed}.
    const { isConfirmed } = await confirmDialog({
      text: `${verb} — ${cfg.label} for ${r.centre_name}?`,
      confirmButtonText: 'Yes',
      options: { title: 'Please confirm' }
    })
    if (!isConfirmed) return
    setBusyId(r.centre)
    try {
      // PM and consumables share the send/receive chain (approve -> send ->
      // centre confirms receipt); each has its own decision endpoint.
      if (category === 'pm') await decideOrder(r.centre, monthDate, decision)
      else await decideConsumables(r.centre, monthDate, decision)
      notify(`${cfg.label} ${decision} for ${r.centre_name}`, '', 'success')
      load()
    } finally {
      setBusyId(null)
    }
  }
  const toggleFreeze = async (r, frozen) => {
    setBusyId(r.centre)
    try {
      await setPettyCashFreeze(r.centre, monthDate, !frozen)
      notify(
        `${r.centre_name} ${frozen ? 'unfrozen' : 'frozen'}`,
        '',
        'success'
      )
      load()
    } finally {
      setBusyId(null)
    }
  }
  const saveAlloc = async (r) => {
    const amount = allocEdits[r.centre]
    if (amount === undefined || amount === '') return
    setBusyId(r.centre)
    try {
      await savePettyCashAllocation({
        centre: r.centre,
        month: monthDate,
        amount: Number(amount)
      })
      notify(`Allocation saved for ${r.centre_name}`, '', 'success')
      load()
    } finally {
      setBusyId(null)
    }
  }

  // Open the "view list" popup for a centre and fetch its lines. The row rides
  // along so the PO export can carry its PO number and submitter.
  const openDetail = async (r) => {
    setDetail({ centre_name: r.centre_name, row: r, data: null })
    try {
      const data = await fetchHqMonthlyBoardDetail(
        monthDate,
        r.centre,
        category
      )
      setDetail({ centre_name: r.centre_name, row: r, data })
    } catch (e) {
      setDetail({
        centre_name: r.centre_name,
        row: r,
        data: { columns: [], rows: [], total: '0' }
      })
    }
  }

  // Open the bills popup for a centre (ledger categories only). The petty-cash
  // list endpoint already carries `invoice_count` per line, and HQ users are
  // unscoped, so this needs no new backend route.
  const openBills = async (r) => {
    setBills({ centre_name: r.centre_name, rows: null })
    try {
      const res = await fetchPettyCash({
        centre: r.centre,
        month: monthDate,
        flow: category === 'rm' ? 'repair_maintenance' : 'petty_cash'
      })
      setBills({ centre_name: r.centre_name, rows: res?.results || [] })
    } catch (e) {
      setBills({ centre_name: r.centre_name, rows: [] })
    }
  }

  // Open the status-tracking popup for a centre (PM/consumables only).
  const openTrack = async (r) => {
    setTrack({ centre_name: r.centre_name, row: r, data: null })
    try {
      const data = await fetchHqOrderTimeline(monthDate, r.centre, category)
      setTrack({ centre_name: r.centre_name, row: r, data })
      setRemarkDraft(data.review_remarks || '')
    } catch (e) {
      setTrack({
        centre_name: r.centre_name,
        row: r,
        data: { exists: false, stages: [] }
      })
    }
  }

  const saveRemark = async () => {
    setSavingTrack(true)
    try {
      await hqOrderAction({
        month: monthDate,
        centre: track.row.centre,
        category,
        action: 'remark',
        remarks: remarkDraft
      })
      notify('HQ remark saved', '', 'success')
    } finally {
      setSavingTrack(false)
    }
  }

  const resendMaterial = async () => {
    const { isConfirmed } = await confirmDialog({
      text: `Resend the flagged (discrepancy / not-received) items to ${track.centre_name}? They'll be asked to confirm receipt of just those items again.`,
      confirmButtonText: 'Yes',
      options: { title: 'Please confirm' }
    })
    if (!isConfirmed) return
    setSavingTrack(true)
    try {
      await hqOrderAction({
        month: monthDate,
        centre: track.row.centre,
        category,
        action: 'resend',
        remarks: remarkDraft
      })
      notify('Material resent — centre notified', '', 'success')
      setTrack(null)
      load()
    } finally {
      setSavingTrack(false)
    }
  }

  // Export payload for the open detail popup (Excel/PDF of the full line list).
  const detailExport = () => {
    const d = (detail && detail.data) || { columns: [], rows: [] }
    return {
      filename: `${category}-${
        detail ? detail.centre_name : ''
      }-${month}`.replace(/\s+/g, '-'),
      title: `${cfg.label} — ${
        detail ? detail.centre_name : ''
      } · ${monthLabel}`,
      headers: d.columns,
      rows: d.rows
    }
  }

  // One centre's order as a PO document spec (order categories only).
  const orderSpec = (centreName, po, columns, rows, submittedBy) => {
    const regRows = registerRows(columns, rows)
    return {
      title: `${cfg.label} order`,
      subtitle: `${centreName} — ${monthLabel}`,
      po: po || undefined,
      meta: [
        { k: 'Centre', v: centreName },
        { k: 'Month', v: monthLabel },
        { k: 'Items', v: String(rows.length) },
        submittedBy ? { k: 'Submitted by', v: submittedBy } : null
      ].filter(Boolean),
      headers: registerHeaders(columns),
      rows: regRows,
      align: registerAlign(columns),
      totals: registerTotals(regRows, columns)
    }
  }

  // The detail popup's download as a PO (order categories).
  const detailOrderExport = (kind) => {
    const d = detail.data
    const v = vm(detail.row)
    const fname = `${category}-PO-${detail.centre_name}-${month}`.replace(
      /\s+/g,
      '-'
    )
    const spec = orderSpec(
      detail.centre_name,
      v.po,
      d.columns,
      d.rows,
      v.submitted
    )
    if (kind === 'excel') {
      downloadXlsxSheets(fname, [
        {
          name: detail.centre_name,
          pre: registerPre(cfg.label, v.po, detail.centre_name, monthLabel),
          headers: registerHeaders(d.columns),
          rows: [...spec.rows, [], spec.totals]
        }
      ])
    } else {
      downloadOrderPdf(fname, spec)
    }
  }

  // Detailed HQ export (Azam 2026-07-21): a Summary + each centre's item detail,
  // as a multi-tab Excel and a multi-page PDF (summary page, then one per centre).
  const totalRow = (c) => {
    const row = new Array(c.columns.length).fill('')
    row[0] = 'Total'
    row[c.columns.length - 1] = c.total
    return row
  }
  const runExport = async (kind) => {
    setExporting(true)
    try {
      const d = await fetchHqBoardExport(monthDate, category)
      const fname = `${category}-${month}`
      if (cfg.ledger) {
        // Ledgers have no PO — the plain summary + per-centre dump stands.
        if (kind === 'excel') {
          downloadXlsxSheets(fname, [
            {
              name: 'Summary',
              headers: d.summary.columns,
              rows: d.summary.rows
            },
            ...d.centres.map((c) => ({
              name: c.name,
              headers: c.columns,
              rows: [...c.rows, totalRow(c)]
            }))
          ])
        } else {
          downloadPdfSections(fname, [
            {
              title: `${cfg.label} — ${monthLabel} · Summary`,
              headers: d.summary.columns,
              rows: d.summary.rows
            },
            ...d.centres.map((c) => ({
              title: `${c.name} · ${monthLabel}`,
              headers: c.columns,
              rows: [...c.rows, totalRow(c)]
            }))
          ])
        }
        return
      }
      // Order categories: Summary, then each filled centre as its own PO.
      const poByName = {}
      const byName = {}
      list.forEach((r) => {
        poByName[r.centre_name] = vm(r).po || ''
        byName[r.centre_name] = vm(r)
      })
      const filled = d.centres.filter((c) => (c.rows || []).length)
      if (kind === 'excel') {
        downloadXlsxSheets(fname, [
          { name: 'Summary', headers: d.summary.columns, rows: d.summary.rows },
          ...filled.map((c) => {
            const regRows = registerRows(c.columns, c.rows)
            return {
              name: c.name,
              pre: registerPre(cfg.label, poByName[c.name], c.name, monthLabel),
              headers: registerHeaders(c.columns),
              rows: [...regRows, [], registerTotals(regRows, c.columns)]
            }
          })
        ])
      } else {
        downloadOrderPdfSections(fname, [
          {
            title: `${cfg.label} — ${monthLabel} · Summary`,
            headers: d.summary.columns,
            rows: d.summary.rows,
            signoff: false
          },
          ...filled.map((c) =>
            orderSpec(
              c.name,
              poByName[c.name],
              c.columns,
              c.rows,
              (byName[c.name] || {}).submitted
            )
          )
        ])
      }
    } catch (e) {
      notify('Could not build the export', '', 'error')
    } finally {
      setExporting(false)
    }
  }

  const exportData = () => ({
    filename: `${category}-${month}`,
    title: `${cfg.label} — ${monthLabel}`,
    headers: [
      'Sl No.',
      'Centre',
      'PO',
      'Month',
      'Items',
      'Total',
      'Status',
      cfg.ledger ? 'Entered by' : 'Submitted by'
    ],
    rows: list.map((r, i) => {
      const v = vm(r)
      return [
        String(i + 1),
        r.centre_name,
        v.po || '',
        monthLabel,
        v.items == null ? '' : String(v.items),
        String(v.total || 0),
        (META[v.status] || {}).label || v.status,
        v.submitted || ''
      ]
    })
  })

  return (
    <Box>
      <div className={classes.bar}>
        <TextField
          type="month"
          size="small"
          label="Month"
          value={month}
          onChange={(e) => setMonth(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        {rows && (
          <div className={classes.summary}>
            <Chip
              size="small"
              label={`${notFilled} not filled`}
              style={{
                background: 'transparent',
                border: '1px dashed #c9791f',
                color: '#c9791f',
                fontWeight: 600
              }}
            />
            {!cfg.ledger && (
              <Chip
                size="small"
                label={`${waiting} waiting for HQ`}
                style={{
                  background: '#fff8e1',
                  color: '#8d6e00',
                  fontWeight: 600
                }}
              />
            )}
          </div>
        )}
        <div className={classes.spacer} />
        {category === 'rm' && (
          <>
            <Button
              size="small"
              variant="outlined"
              style={{ marginRight: 8 }}
              onClick={() => setRmView('azam')}
            >
              Azam&apos;s sheet
            </Button>
            <Button
              size="small"
              variant="outlined"
              style={{ marginRight: 8 }}
              onClick={() => setRmView('reco')}
            >
              Reco
            </Button>
          </>
        )}
        <Button
          size="small"
          variant="outlined"
          disabled={exporting}
          onClick={() => runExport('excel')}
        >
          ⬇ Excel
        </Button>
        <Button
          size="small"
          variant="outlined"
          disabled={exporting}
          style={{ marginLeft: 8 }}
          onClick={() => runExport('pdf')}
        >
          ⬇ PDF
        </Button>
      </div>

      {loading && <LinearProgress />}
      {rows && (
        <Paper variant="outlined" style={{ overflow: 'visible' }}>
          <Table size="small" className={classes.stickyTable}>
            <TableHead>
              <TableRow>
                <TableCell className={`${classes.cell} ${classes.frzHead1}`}>
                  Sl No.
                </TableCell>
                <TableCell className={`${classes.cell} ${classes.frzHead2}`}>
                  Centre
                </TableCell>
                <TableCell className={classes.cell}>PO</TableCell>
                <TableCell className={classes.cell}>Month</TableCell>
                <TableCell className={classes.cell}>Items</TableCell>
                <TableCell className={classes.cell}>Total</TableCell>
                <TableCell className={classes.cell}>Status</TableCell>
                <TableCell className={classes.cell}>
                  {cfg.ledger ? 'Entered by' : 'Submitted by'}
                </TableCell>
                <TableCell className={classes.cell}>View</TableCell>
                <TableCell className={classes.cell}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {list.map((r, idx) => {
                const v = vm(r)
                return (
                  <TableRow key={r.centre} hover>
                    <TableCell className={`${classes.cell} ${classes.frz1}`}>
                      {idx + 1}
                    </TableCell>
                    <TableCell className={`${classes.cell} ${classes.frz2}`}>
                      <span className={classes.centre}>{r.centre_name}</span>
                    </TableCell>
                    <TableCell className={`${classes.cell} ${classes.poCell}`}>
                      {v.po || '—'}
                    </TableCell>
                    <TableCell className={classes.cell}>{monthLabel}</TableCell>
                    <TableCell className={classes.cell}>
                      {v.items == null ? '—' : v.items}
                    </TableCell>
                    <TableCell className={classes.cell}>
                      {money(v.total)}
                      {cfg.alloc && (
                        <div className={classes.meta}>
                          allocated {money(v.allocation)}
                        </div>
                      )}
                      {/* Draft sidecar: rows the centre has typed + saved but
                          not submitted — planned spend, never added to total. */}
                      {Number(v.inDraft || 0) > 0 && (
                        <div
                          className={classes.meta}
                          style={{ color: '#ef6c00' }}
                        >
                          + {money(v.inDraft)} in draft
                        </div>
                      )}
                    </TableCell>
                    <TableCell className={classes.cell}>
                      {cfg.ledger ? (
                        <StatusChip s={v.status} />
                      ) : (
                        <span
                          style={{ cursor: 'pointer' }}
                          title="View status tracking"
                          onClick={() => openTrack(r)}
                        >
                          <StatusChip s={v.status} />
                        </span>
                      )}
                    </TableCell>
                    <TableCell className={classes.cell}>
                      {v.submitted || '—'}
                    </TableCell>
                    <TableCell className={classes.cell}>
                      <Button size="small" onClick={() => openDetail(r)}>
                        View
                      </Button>
                    </TableCell>
                    <TableCell className={classes.cell}>
                      {hasMenu(v) && (
                        <IconButton
                          size="small"
                          disabled={busyId === r.centre}
                          title="Actions"
                          onClick={(e) =>
                            setMenu({ el: e.currentTarget, row: r })
                          }
                        >
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                )
              })}
              {!list.length && (
                <TableRow>
                  <TableCell colSpan={10} className={classes.cell}>
                    <Typography variant="body2" color="textSecondary">
                      No centres to show.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Paper>
      )}

      {menu && (
        <Menu anchorEl={menu.el} open onClose={() => setMenu(null)}>
          {!cfg.ledger && vm(menu.row).status === 'submitted' && (
            <MenuItem
              onClick={() => {
                decide(menu.row, 'approved')
                setMenu(null)
              }}
            >
              Approve
            </MenuItem>
          )}
          {!cfg.ledger && vm(menu.row).status === 'submitted' && (
            <MenuItem
              style={{ color: '#c62828' }}
              onClick={() => {
                decide(menu.row, 'rejected')
                setMenu(null)
              }}
            >
              Reject
            </MenuItem>
          )}
          {(category === 'pm' || category === 'consumables') &&
            vm(menu.row).status === 'approved' && (
              <MenuItem
                onClick={() => {
                  decide(menu.row, 'sent')
                  setMenu(null)
                }}
              >
                Send to centre
              </MenuItem>
            )}
          {canReopen(vm(menu.row)) && (
            <MenuItem
              onClick={() => {
                reopenPm(menu.row)
                setMenu(null)
              }}
            >
              Reopen for the centre
            </MenuItem>
          )}
          {cfg.alloc && (
            <MenuItem
              onClick={(e) => {
                setAllocPop({ el: e.currentTarget, row: menu.row })
                setMenu(null)
              }}
            >
              Set allocation…
            </MenuItem>
          )}
          {cfg.ledger && (
            <MenuItem
              onClick={() => {
                openBills(menu.row)
                setMenu(null)
              }}
            >
              View bills…
            </MenuItem>
          )}
          {cfg.ledger && (
            <MenuItem
              onClick={() => {
                toggleFreeze(menu.row, vm(menu.row).frozen)
                setMenu(null)
              }}
            >
              {vm(menu.row).frozen ? 'Unfreeze' : 'Freeze'}
            </MenuItem>
          )}
        </Menu>
      )}

      {allocPop && (
        <Popover
          anchorEl={allocPop.el}
          open
          onClose={() => setAllocPop(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Box
            style={{
              padding: 12,
              display: 'flex',
              gap: 8,
              alignItems: 'center'
            }}
          >
            <TextField
              className={classes.alloc}
              size="small"
              type="number"
              placeholder="allocate"
              value={allocEdits[allocPop.row.centre] ?? ''}
              onChange={(e) =>
                setAllocEdits((p) => ({
                  ...p,
                  [allocPop.row.centre]: e.target.value
                }))
              }
            />
            <Button
              size="small"
              color="primary"
              variant="contained"
              disableElevation
              disabled={
                busyId === allocPop.row.centre ||
                (allocEdits[allocPop.row.centre] ?? '') === ''
              }
              onClick={() => {
                saveAlloc(allocPop.row)
                setAllocPop(null)
              }}
            >
              Save
            </Button>
          </Box>
        </Popover>
      )}

      <Dialog
        open={!!track}
        onClose={() => setTrack(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {cfg.label} — {track ? track.centre_name : ''} · {monthLabel}
        </DialogTitle>
        <DialogContent dividers>
          {!track || !track.data ? (
            <LinearProgress />
          ) : !track.data.exists ? (
            <Typography variant="body2" color="textSecondary">
              This centre hasn&apos;t submitted for this month yet.
            </Typography>
          ) : (
            <Box>
              <Box
                style={{ display: 'flex', flexDirection: 'column', gap: 10 }}
              >
                {track.data.stages.map((s) => (
                  <Box
                    key={s.key}
                    style={{ display: 'flex', gap: 10, alignItems: 'baseline' }}
                  >
                    <span
                      style={{
                        color: s.done ? '#2e7d32' : '#c9c6c0',
                        fontSize: 15,
                        lineHeight: 1
                      }}
                    >
                      {s.done ? '●' : '○'}
                    </span>
                    <Box style={{ flex: 1 }}>
                      <Typography
                        variant="body2"
                        style={{
                          fontWeight: 600,
                          color: s.done ? undefined : '#9c988f'
                        }}
                      >
                        {s.label}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {s.done
                          ? `${Dayjs(s.at).format('DD MMM YYYY')}${
                              s.by ? ` · ${s.by}` : ''
                            }`
                          : 'pending'}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
              {track.data.discrepancy && track.data.discrepancy_notes && (
                <Box
                  style={{
                    marginTop: 12,
                    padding: 8,
                    borderRadius: 4,
                    background: '#ffebee',
                    color: '#c62828'
                  }}
                >
                  <Typography variant="caption">
                    Discrepancy: {track.data.discrepancy_notes}
                  </Typography>
                </Box>
              )}
              <TextField
                label="HQ remark"
                placeholder="Note back to the centre…"
                multiline
                minRows={2}
                fullWidth
                variant="outlined"
                size="small"
                style={{ marginTop: 14 }}
                value={remarkDraft}
                onChange={(e) => setRemarkDraft(e.target.value)}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {track && track.data && track.data.exists && (
            <>
              <Button onClick={saveRemark} disabled={savingTrack}>
                Save remark
              </Button>
              {track.data.can_resend && (
                <Button
                  color="primary"
                  variant="contained"
                  disableElevation
                  disabled={savingTrack}
                  onClick={resendMaterial}
                >
                  Resend flagged items
                </Button>
              )}
            </>
          )}
          <Button onClick={() => setTrack(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!detail}
        onClose={() => setDetail(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {cfg.label} — {detail ? detail.centre_name : ''} · {monthLabel}
        </DialogTitle>
        <DialogContent dividers>
          {!detail || !detail.data ? (
            <LinearProgress />
          ) : !detail.data.rows.length ? (
            <Typography variant="body2" color="textSecondary">
              No lines for this centre this month.
            </Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {detail.data.columns.map((c) => (
                      <TableCell key={c} className={classes.cell}>
                        {c}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {detail.data.rows.map((row, i) => (
                    <TableRow key={i} hover>
                      {row.map((cell, j) => (
                        <TableCell key={j} className={classes.cell}>
                          {/* A reference link is a 300-character Amazon URL —
                              printed raw it destroys every column width, which
                              is exactly what it was doing inside Remarks. */}
                          {isUrl(cell) ? (
                            <a
                              href={String(cell).trim()}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              view
                            </a>
                          ) : (
                            cell
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          {detail && detail.data && (
            <Typography variant="subtitle2" style={{ marginTop: 12 }}>
              Total: {money(detail.data.total)}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          {detail &&
            detail.data &&
            detail.data.rows.length > 0 &&
            (cfg.ledger ? (
              <ExportButtons getData={detailExport} />
            ) : (
              <>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => detailOrderExport('excel')}
                >
                  ⬇ Excel
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  style={{ marginLeft: 8 }}
                  onClick={() => detailOrderExport('pdf')}
                >
                  ⬇ PDF
                </Button>
              </>
            ))}
          <Button onClick={() => setDetail(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Ledger categories: the centre's spend lines and the bills attached to
          each. Read-only — HQ verifies proof, it never uploads for a centre. */}
      <Dialog
        open={!!bills}
        onClose={() => {
          setBills(null)
          setBillEntry(null)
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Bills — {bills ? bills.centre_name : ''} · {monthLabel}
        </DialogTitle>
        <DialogContent dividers>
          {!bills || !bills.rows ? (
            <LinearProgress />
          ) : !bills.rows.length ? (
            <Typography variant="body2" color="textSecondary">
              No entries for this centre this month.
            </Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell className={classes.cell}>Date</TableCell>
                    <TableCell className={classes.cell}>Category</TableCell>
                    <TableCell className={classes.cell}>Description</TableCell>
                    <TableCell className={classes.cell} align="right">
                      Amount
                    </TableCell>
                    <TableCell className={classes.cell}>Entered by</TableCell>
                    {category === 'rm' && (
                      <TableCell className={classes.cell}>Paid by</TableCell>
                    )}
                    <TableCell className={classes.cell} align="center">
                      Bills
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {bills.rows.map((row) => (
                    <TableRow key={row.uuid} hover>
                      <TableCell className={classes.cell}>
                        {Dayjs(row.date).format('D MMM YYYY')}
                      </TableCell>
                      <TableCell className={classes.cell}>
                        {row.category || '—'}
                      </TableCell>
                      <TableCell className={classes.cell}>
                        {row.description || '—'}
                      </TableCell>
                      <TableCell className={classes.cell} align="right">
                        {money(row.amount)}
                      </TableCell>
                      <TableCell className={classes.cell}>
                        {row.created_by_name || '—'}
                      </TableCell>
                      {category === 'rm' && (
                        <TableCell className={classes.cell}>
                          {rmPaidByLabel(row.paid_by)}
                        </TableCell>
                      )}
                      <TableCell className={classes.cell} align="center">
                        {row.invoice_count ? (
                          <Button
                            size="small"
                            color="primary"
                            onClick={() => setBillEntry(row)}
                          >
                            View ({row.invoice_count})
                          </Button>
                        ) : (
                          <Typography variant="caption" color="error">
                            None
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          {billEntry && (
            <Box mt={2}>
              <AttachmentUploader
                readOnly
                entityType="petty_cash_entry"
                entry={billEntry.uuid}
                label={`Bills for ${Dayjs(billEntry.date).format(
                  'D MMM YYYY'
                )} · ${money(billEntry.amount)}`}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setBills(null)
              setBillEntry(null)
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
      {category === 'rm' && (
        <Dialog
          open={!!rmView}
          onClose={() => {
            setRmView(null)
            load()
          }}
          fullScreen
          // Full screen so the tables' searchable header sticks to the top of
          // the screen while scrolling. Dialogs render outside the
          // procurement lowercase wrapper.
          PaperProps={{ style: { textTransform: 'lowercase' } }}
        >
          <DialogContent style={{ padding: 0 }}>
            {rmView === 'azam' && <HqRmRegister initialMonth={month} />}
            {rmView === 'reco' && <HqRmReco initialMonth={month} />}
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setRmView(null)
                load()
              }}
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  )
}

export default HqMonthlyBoard
